import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Truck, Search, Zap, History, CheckCircle2, XCircle, Calendar, Fuel, User, FileText } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VehicleRCVerificationPage = () => {
  const { user } = useAuth();
  const [apiHistory, setApiHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [apiResult, setApiResult] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [registrationNumber, setRegistrationNumber] = useState("");

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/verify/history?verification_type=vehicle`);
      setApiHistory(response.data);
    } catch (error) {
      console.error("Failed to fetch history");
    } finally {
      setLoading(false);
    }
  };

  const handleApiVerify = async (e) => {
    e.preventDefault();
    if (!registrationNumber || registrationNumber.length < 6) {
      toast.error("Please enter a valid vehicle registration number");
      return;
    }
    
    setVerifying(true);
    setApiResult(null);
    
    try {
      const response = await axios.post(`${API_URL}/verify/vehicle-rc`, {
        registration_number: registrationNumber.toUpperCase()
      });
      setApiResult(response.data);
      if (response.data.success && response.data.valid) {
        toast.success("Vehicle RC verified successfully!");
      } else {
        toast.error(response.data.message || "Vehicle RC verification failed");
      }
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "API verification failed");
      setApiResult({ success: false, message: error.response?.data?.detail || "Verification failed" });
    } finally {
      setVerifying(false);
    }
  };

  const viewDetails = (record) => {
    setSelectedRecord(record);
    setDetailsDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      verified: "border-green-500/50 text-green-400 bg-green-500/10",
      invalid: "border-red-500/50 text-red-400 bg-red-500/10",
      pending: "border-yellow-500/50 text-yellow-400 bg-yellow-500/10"
    };
    return styles[status] || styles.pending;
  };

  const filteredHistory = apiHistory.filter(record =>
    record.registration_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.owner_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-primary animate-pulse font-heading text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="vehicle-rc-verification-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-3">
            <Truck className="w-8 h-8 text-primary" />
            Vehicle RC Verification
          </h1>
          <p className="text-muted-foreground mt-1">Verify vehicle registration certificates using Vahan database</p>
        </div>
        <Button 
          onClick={() => setVerifyDialogOpen(true)}
          className="glow-primary bg-primary hover:bg-primary/90"
          data-testid="verify-rc-btn"
        >
          <Zap className="w-4 h-4 mr-2" />
          Verify Vehicle RC
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Total Verifications</p>
                <p className="font-heading text-2xl font-bold text-foreground">{apiHistory.length}</p>
              </div>
              <History className="w-8 h-8 text-muted-foreground/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Verified</p>
                <p className="font-heading text-2xl font-bold text-green-400">
                  {apiHistory.filter(r => r.status === "verified").length}
                </p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-400/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Invalid</p>
                <p className="font-heading text-2xl font-bold text-red-400">
                  {apiHistory.filter(r => r.status === "invalid").length}
                </p>
              </div>
              <XCircle className="w-8 h-8 text-red-400/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="border-border bg-card">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by registration number or owner name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-secondary/50 border-border"
              data-testid="rc-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Verification History Table */}
      <Card className="border-border bg-card">
        <CardHeader className="border-b border-border/50">
          <CardTitle className="font-heading flex items-center gap-2">
            <History className="w-5 h-5" />
            Verification History ({filteredHistory.length})
          </CardTitle>
          <CardDescription>All vehicle RC verifications via Sandbox API (Vahan data)</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Reg. Number</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Owner Name</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Vehicle Class</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Fuel Type</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Status</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Verified By</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Date</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredHistory.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No verification history yet. Click "Verify Vehicle RC" to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredHistory.map((record) => (
                    <TableRow key={record.id} className="border-border table-row-hover">
                      <TableCell className="font-mono font-bold text-primary">{record.registration_number}</TableCell>
                      <TableCell className="text-foreground">{record.owner_name || "-"}</TableCell>
                      <TableCell className="text-muted-foreground">{record.vehicle_class || "-"}</TableCell>
                      <TableCell className="text-muted-foreground">{record.fuel_type || "-"}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={getStatusBadge(record.status)}>
                          {record.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{record.verified_by}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {new Date(record.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewDetails(record)}
                          className="text-primary hover:text-primary/80"
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Verify Dialog */}
      <Dialog open={verifyDialogOpen} onOpenChange={setVerifyDialogOpen}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Zap className="w-5 h-5 text-primary" />
              Vehicle RC Verification
            </DialogTitle>
            <DialogDescription>Verify vehicle registration using Vahan database via Sandbox API</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleApiVerify} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="registration_number">Vehicle Registration Number</Label>
              <Input
                id="registration_number"
                value={registrationNumber}
                onChange={(e) => setRegistrationNumber(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                className="bg-secondary/50 border-border font-mono text-lg tracking-widest text-center"
                placeholder="MH12AB1234"
                maxLength={12}
                required
              />
              <p className="text-xs text-muted-foreground text-center">Enter vehicle registration number (e.g., MH12AB1234)</p>
            </div>

            {apiResult && (
              <div className={`p-4 rounded-lg border ${apiResult.success && apiResult.valid ? 'bg-green-500/10 border-green-500/50' : 'bg-red-500/10 border-red-500/50'}`}>
                <div className="flex items-center gap-2 mb-3">
                  {apiResult.success && apiResult.valid ? (
                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-400" />
                  )}
                  <span className={`font-semibold ${apiResult.success && apiResult.valid ? 'text-green-400' : 'text-red-400'}`}>
                    {apiResult.success && apiResult.valid ? 'Vehicle Verified' : 'Verification Failed'}
                  </span>
                </div>
                {apiResult.success && apiResult.valid && (
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-muted-foreground text-xs">Owner</p>
                        <p className="text-foreground font-medium">{apiResult.owner_name || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Truck className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-muted-foreground text-xs">Vehicle Class</p>
                        <p className="text-foreground">{apiResult.vehicle_class || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Fuel className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-muted-foreground text-xs">Fuel Type</p>
                        <p className="text-foreground">{apiResult.fuel_type || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-muted-foreground text-xs">Registration Date</p>
                        <p className="text-foreground">{apiResult.registration_date || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="col-span-2">
                      <p className="text-muted-foreground text-xs">Maker/Model</p>
                      <p className="text-foreground">{apiResult.maker_model || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Fitness Valid Until</p>
                      <p className="text-foreground">{apiResult.fitness_upto || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Insurance Valid Until</p>
                      <p className="text-foreground">{apiResult.insurance_upto || 'N/A'}</p>
                    </div>
                  </div>
                )}
                {!apiResult.success && (
                  <p className="text-sm text-muted-foreground">{apiResult.message}</p>
                )}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setVerifyDialogOpen(false); setApiResult(null); setRegistrationNumber(""); }}>
                Close
              </Button>
              <Button type="submit" className="glow-primary bg-primary" disabled={verifying}>
                {verifying ? (
                  <>
                    <span className="animate-spin mr-2">⟳</span>
                    Verifying...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-2" />
                    Verify RC
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Vehicle Details
            </DialogTitle>
            <DialogDescription>Registration: {selectedRecord?.registration_number}</DialogDescription>
          </DialogHeader>
          {selectedRecord && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-muted-foreground text-xs uppercase tracking-wider">Owner Name</p>
                  <p className="text-foreground font-medium">{selectedRecord.owner_name || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground text-xs uppercase tracking-wider">Vehicle Class</p>
                  <p className="text-foreground">{selectedRecord.vehicle_class || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground text-xs uppercase tracking-wider">Fuel Type</p>
                  <p className="text-foreground">{selectedRecord.fuel_type || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground text-xs uppercase tracking-wider">Maker/Model</p>
                  <p className="text-foreground">{selectedRecord.maker_model || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground text-xs uppercase tracking-wider">Registration Date</p>
                  <p className="text-foreground">{selectedRecord.registration_date || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground text-xs uppercase tracking-wider">RTO</p>
                  <p className="text-foreground">{selectedRecord.api_response?.rto || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground text-xs uppercase tracking-wider">Fitness Valid Until</p>
                  <p className="text-foreground">{selectedRecord.fitness_upto || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground text-xs uppercase tracking-wider">Insurance Valid Until</p>
                  <p className="text-foreground">{selectedRecord.insurance_upto || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground text-xs uppercase tracking-wider">Chassis Number</p>
                  <p className="text-foreground font-mono text-sm">{selectedRecord.api_response?.chassis_number || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground text-xs uppercase tracking-wider">Engine Number</p>
                  <p className="text-foreground font-mono text-sm">{selectedRecord.api_response?.engine_number || 'N/A'}</p>
                </div>
              </div>
              <div className="pt-4 border-t border-border">
                <p className="text-muted-foreground text-xs">
                  Verified by <span className="text-foreground">{selectedRecord.verified_by}</span> on {new Date(selectedRecord.created_at).toLocaleString()}
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VehicleRCVerificationPage;
